import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import LoginPage from "./pages/login";
import AuthPage from "./pages/auth";
import GoogleRegister from "./pages/GoogleRegister";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ClientPage from "./pages/main";
import PrivateRoute from "./components/privateroute"; // Імпортуємо компонент для перевірки авторизації
import AppLockGate from "./components/AppLockGate";
import DockScrollHints from "./components/DockScrollHints";
import { WebSocketProvider } from "./context/WebSocketContext";
import { initPushNotifications } from "./utils/pushInit";
import { initAndroidBackButton, pushAndroidBackHandler } from "./utils/androidBack";
import { initNotificationSound } from "./utils/notificationSound";
import "./styles/client-modern.css";
import "./styles/client-mobile-shell.css";

function isCapacitorRuntime() {
  return Boolean(window?.Capacitor?.isNativePlatform?.());
}

function normalizePushKind(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";

  if (["news", "news_item", "announcement"].includes(raw)) return "news";
  if (["operator_info", "operator-info", "info", "information"].includes(raw)) return "operator_info";
  if (["chat", "message", "messages"].includes(raw)) return "chat";
  if (["poll", "polls", "survey"].includes(raw)) return "poll";
  if (["request", "requests", "request_response", "request_reply"].includes(raw)) return "request";
  if (["feedback", "feedback_response", "feedback_reply"].includes(raw)) return "feedback";
  if (["sensor", "sensor_alert", "sensor-alert", "iot_alert", "iot"].includes(raw)) return "sensor_alert";
  if (["access_request", "client_access_request", "access-request", "client-access-request"].includes(raw)) {
    return "access_request";
  }

  return raw;
}

function normalizePushPayload(payload) {
  const source = payload && typeof payload === "object" ? payload : {};
  const pick = (...keys) => {
    for (const key of keys) {
      const value = source?.[key];
      if (value === undefined || value === null) continue;
      const normalized = String(value).trim();
      if (normalized) return normalized;
    }
    return "";
  };

  let kind = normalizePushKind(pick("kind", "type", "notification_kind", "category"));
  const news_id = pick("news_id", "newsId");
  const info_id = pick("info_id", "operator_info_id", "operatorInfoId");
  const head_id = pick("head_id", "headId", "chat_id", "chatId", "conversation_id", "conversationId");
  const poll_id = pick("poll_id", "pollId");
  const request_id = pick("request_id", "requestId");
  const feedback_id = pick("feedback_id", "feedbackId");
  const sensor_id = pick("sensor_id", "sensorId");
  const event_id = pick("event_id", "eventId");
  const access_request_id = pick("access_request_id", "accessRequestId", "client_access_request_id");

  if (!kind) {
    if (news_id) kind = "news";
    else if (info_id) kind = "operator_info";
    else if (head_id) kind = "chat";
    else if (poll_id) kind = "poll";
    else if (request_id) kind = "request";
    else if (feedback_id) kind = "feedback";
    else if (sensor_id) kind = "sensor_alert";
    else if (access_request_id) kind = "access_request";
  }

  if (!kind) return null;

  const out = { kind };
  if (news_id) out.news_id = news_id;
  if (info_id) out.info_id = info_id;
  if (head_id) out.head_id = head_id;
  if (poll_id) out.poll_id = poll_id;
  if (request_id) out.request_id = request_id;
  if (feedback_id) out.feedback_id = feedback_id;
  if (sensor_id) out.sensor_id = sensor_id;
  if (event_id) out.event_id = event_id;
  if (access_request_id) out.access_request_id = access_request_id;
  return out;
}

function parsePushDeepLink(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.protocol !== "wra-client:") return null;
    if (u.hostname !== "push") return null;

    return normalizePushPayload({
      kind: u.searchParams.get("kind"),
      news_id: u.searchParams.get("news_id"),
      info_id: u.searchParams.get("info_id"),
      head_id: u.searchParams.get("head_id"),
      poll_id: u.searchParams.get("poll_id"),
      request_id: u.searchParams.get("request_id"),
      feedback_id: u.searchParams.get("feedback_id"),
      sensor_id: u.searchParams.get("sensor_id"),
      event_id: u.searchParams.get("event_id"),
      access_request_id: u.searchParams.get("access_request_id"),
    });
  } catch (_) {
    return null;
  }
}

const DeepLinkHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isCapacitorRuntime()) return;

    const handleUrl = (url) => {
      const parsed = parsePushDeepLink(url);
      if (!parsed) return;
      try {
        localStorage.setItem("WR_PENDING_PUSH_OPEN", JSON.stringify(parsed));
      } catch (_) {
        // ignore
      }

      try {
        window.dispatchEvent(new Event("wrPushOpen"));
      } catch (_) {
        // ignore
      }

      if (location?.pathname !== "/main") {
        navigate("/main");
      }
    };

    const onNativePushUrl = (event) => {
      handleUrl(event?.detail?.url);
    };
    window.addEventListener("wrNativePushUrl", onNativePushUrl);

    const App = window?.Capacitor?.Plugins?.App;
    let sub = null;

    if (App?.addListener) {
      (async () => {
        try {
          const res = await App?.getLaunchUrl?.();
          if (res?.url) handleUrl(res.url);
        } catch (_) {
          // ignore
        }
      })();

      sub = App.addListener("appUrlOpen", (event) => {
        handleUrl(event?.url);
      });
    }

    return () => {
      window.removeEventListener("wrNativePushUrl", onNativePushUrl);
      try {
        sub?.remove?.();
      } catch (_) {
        // ignore
      }
    };
  }, [navigate, location?.pathname]);

  return null;
};

function AndroidBackRouterBridge() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    return pushAndroidBackHandler(() => {
      if (location.pathname === "/main") {
        return false;
      }
      if (location.pathname !== "/") {
        navigate(-1);
        return true;
      }
      return false;
    });
  }, [location.pathname, navigate]);

  return null;
}

const App = () => {
  useEffect(() => {
    if (isCapacitorRuntime()) {
      document.documentElement.classList.add("capacitor-native");
    }
    initAndroidBackButton();
    initNotificationSound();
    initPushNotifications();
    return () => {
      document.documentElement.classList.remove("capacitor-native");
    };
  }, []);

  return (
    <BrowserRouter>
      <DeepLinkHandler />
      <AndroidBackRouterBridge />
      <DockScrollHints />
      <WebSocketProvider>
        <AppLockGate>
          <Routes>
            <Route path="/" element={<LoginPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/google-register" element={<GoogleRegister />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            {/* Захищена сторінка Client */}
            <Route
              path="/main"
              element={
                <PrivateRoute>
                  <ClientPage />
                </PrivateRoute>
              }
            />
          </Routes>
        </AppLockGate>
      </WebSocketProvider>
    </BrowserRouter>
  );
};

export default App;
